import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { PlusIcon } from '@heroicons/react/solid';
import { ReceiptTaxIcon, ChevronRightIcon } from '@heroicons/react/outline';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import '../../Pagination.css';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useGetUserTaxesQuery } from '../../Features/taxesSlice';

function ManageTaxesView({}) {
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();
    const [numRowsPerPage, setNumRowsPerPage] = useState(10);
    const [currentItems, setCurrentItems] = useState([]);
    const [pageCount, setPageCount] = useState(0);

    const user = useSelector(selectCurrentUser);

    const {
        data: taxes,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetUserTaxesQuery({ user_id: user.id });

    const [input, setInput] = useState({
        search: ''
    });

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess && !currentItems.length > 0) {
            setCurrentItems(taxes.data);
            setPageCount(Math.ceil(taxes.data.total / numRowsPerPage));
            setProgress(100);
        }

        return () => {};
    }, [taxes]);

    const editTax = (e, id) => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        navigate("/client/edit-tax/" + id);
    };

    const trancateText = (str) => {
        return str.length > 25 ? str.substring(0, 25) + "..." : str;
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <ReceiptTaxIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Taxes
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Manage your tax rates
                            </p>
                        </div>
                    </div>

                    {isSuccess && (
                        <Link
                            to="/client/create-tax"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-colors w-full sm:w-auto"
                        >
                            <PlusIcon className="w-5 h-5" />
                            <span>Add Tax</span>
                        </Link>
                    )}
                </div>

                {/* Main Card */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                    {/* Loading */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                                Loading taxes...
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {isError && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">

                            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <ReceiptTaxIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                No taxes found
                            </h3>

                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                You haven't added any tax rates yet. Create your first tax to get started.
                            </p>

                            <Link
                                to="/client/create-tax"
                                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium transition-colors"
                            >
                                <PlusIcon className="w-5 h-5" />
                                Add Tax
                            </Link>
                        </div>
                    )}

                    {/* Empty State */}
                    {isSuccess && currentItems.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">

                            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <ReceiptTaxIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                No taxes available
                            </h3>

                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Add a tax rate to start managing taxes.
                            </p>

                            <Link
                                to="/client/create-tax"
                                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium transition-colors"
                            >
                                <PlusIcon className="w-5 h-5" />
                                Add Tax
                            </Link>
                        </div>
                    )}

                    {/* Desktop Table */}
                    {isSuccess && currentItems.length > 0 && (
                        <>
                            <div className="hidden md:block overflow-x-auto">

                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="px-6 py-3.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                            >
                                                Name
                                            </th>

                                            <th
                                                scope="col"
                                                className="px-6 py-3.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                            >
                                                Rate
                                            </th>

                                            <th
                                                scope="col"
                                                className="px-6 py-3.5 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                                        {currentItems.map(data => (
                                            <tr
                                                onClick={(e) => editTax(e, data.id)}
                                                key={data.id}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">

                                                        <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                                                            <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
                                                        </div>

                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                {trancateText(data.name)}
                                                            </p>

                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                Tax rate
                                                            </p>
                                                        </div>

                                                    </div>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#80b643]/10 text-[#80b643] text-xs font-semibold">
                                                        {trancateText(data.rate)}%
                                                    </span>
                                                </td>

                                                <td className="px-6 py-4 text-right">
                                                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800">
                                                        <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                    </tbody>
                                </table>

                            </div>

                            {/* Mobile List */}
                            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">

                                {currentItems.map(data => (
                                    <div
                                        key={data.id}
                                        onClick={(e) => editTax(e, data.id)}
                                        className="flex items-center justify-between gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                                    >

                                        <div className="flex items-center gap-3 min-w-0">

                                            <div className="w-10 h-10 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                                                <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
                                            </div>

                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {trancateText(data.name)}
                                                </p>

                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Tax rate
                                                </p>
                                            </div>

                                        </div>

                                        <div className="flex items-center gap-2 flex-shrink-0">

                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#80b643]/10 text-[#80b643] text-xs font-semibold">
                                                {trancateText(data.rate)}%
                                            </span>

                                            <ChevronRightIcon className="w-4 h-4 text-gray-400" />

                                        </div>

                                    </div>
                                ))}

                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}

export default ManageTaxesView;